// Display helpers. Server timestamps are UTC "YYYY-MM-DD HH:MM:SS" strings.

export function parseTime(v: string | null | undefined): Date | null {
  if (!v) return null;
  const d = new Date(v.includes("T") ? v : v.replace(" ", "T") + "Z");
  return Number.isNaN(d.getTime()) ? null : d;
}

export const fmtDate = (v: string | null | undefined) =>
  parseTime(v)?.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) ?? "—";

export const fmtDateTime = (v: string | null | undefined) =>
  parseTime(v)?.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) ?? "—";

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [["year", 31536000], ["month", 2592000], ["week", 604800], ["day", 86400], ["hour", 3600], ["minute", 60], ["second", 1]];

/** "3 days ago", "in 2 hours". */
export function fmtRelative(v: string | null | undefined): string {
  const d = parseTime(v);
  if (!d) return "—";
  const secs = Math.round((d.getTime() - Date.now()) / 1000);
  for (const [unit, size] of UNITS) if (Math.abs(secs) >= size || unit === "second") return rtf.format(Math.round(secs / size), unit);
  return "—";
}

/** Days left until `v` (negative if past). */
export const daysLeft = (v: string) => Math.ceil(((parseTime(v)?.getTime() ?? 0) - Date.now()) / 86400_000);

/** Entitlement expiry label: "Lifetime", "Expired", "Expires today", "12 days left". */
export function fmtExpiry(expiresAt: string | null): string {
  if (expiresAt === null) return "Lifetime";
  const d = daysLeft(expiresAt);
  if (d < 0 || (parseTime(expiresAt)?.getTime() ?? 0) < Date.now()) return "Expired";
  if (d <= 1) return "Expires today";
  return `${d} days left`;
}

export function fmtMoney(cents: number, currency = "GBP"): string {
  try { return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100); }
  catch { return `${currency} ${(cents / 100).toFixed(2)}`; }
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}

export const shortHash = (h: string | null | undefined, n = 10) => (h ? `${h.slice(0, n)}…` : "—");

export const plural = (n: number, word: string, pluralWord = `${word}s`) => `${n} ${n === 1 ? word : pluralWord}`;
